// Norvig — English (translated from no.js master)
// Tone: poetic, editorial, minimalist. Mirror of no.js — every key must exist.
const en = {
  meta: {
    siteTitle: 'Norvig — staffing for health & care',
    siteDescription: 'Norvig connects nurses, care workers and other professionals directly with Norwegian employers. Health & care first — education alongside.',
  },
  common: {
    brandHomeLabel: 'Norvig home',
    learnMore: 'Learn more',
    backToHome: 'Back to Norvig',
    backToTop: 'Back to top',
    soonBadge: 'Coming soon',
  },
  nav: {
    sectors: 'Sectors',
    howItWorks: 'How it works',
    aboutNorway: 'About Norway',
    horisont: 'Horizon',
    aboutUs: 'About us',
    stories: 'Stories',
    candidates: 'Candidates',
    login: 'Log in',
    register: 'Sign up',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    selectLanguage: 'Select language',
    // ─── Facelift Phase 1 editorial nav (short list, 4 items) ───────────
    forProfessionals: 'For professionals',
    forEmployers: 'For employers',
    professionalRole: 'Skilled worker',
    municipalityRole: 'Municipality',
    registerNew: 'Register as new',
    contact: 'Contact',
  },
  auth: {
    common: {
      backHome: 'Back to home',
      brand: {
        eyebrow: 'NORVIG · 2026',
        title: 'A new',
        titleAccent: 'chapter begins.',
        sub: 'Norvig connects skilled professionals directly with Norwegian employers — no middlemen, full follow-up.',
        bullets: [
          'AI-powered matching in seconds',
          'Verified employers — GDPR-compliant',
          'Completely free for skilled workers — open pricing for employers',
        ],
        footnote: 'Built with care for professionals in Norway.',
      },
      orDivider: 'or',
      labels: {
        email: 'Email',
        password: 'Password',
        name: 'Full name',
        role: 'I am a',
        roleProfessional: 'Professional',
        roleEmployer: 'Employer',
      },
      placeholders: {
        email: 'you@email.com',
        password: '••••••••',
        name: 'Your full name',
      },
      genericError: 'Something went wrong. Please try again.',
      rateLimited: 'Too many attempts. Please wait a few minutes and try again.',
    },
    quotes: {
      login: 'Create your new life in Norway.',
      signupProfessional: 'Norway needs you. Norvig helps you find the way.',
      signupEmployer: 'Hiring with intention. Employment that lasts.',
    },
    login: {
      eyebrow: 'Nº 01 — WELCOME BACK',
      title: 'Sign in',
      titleAccent: 'again.',
      sub: 'Pick up where you left off.',
      submit: 'Sign in',
      loading: 'Please wait…',
      lockedOut: 'The account is temporarily locked after too many failed sign-in attempts. Try again in about 15 minutes.',
      google: 'Continue with Google',
      noAccount: 'New here?',
      goToSignup: 'Create account →',
      welcomeBackToast: 'Welcome back.',
      passwordChangedToast: 'Your password was changed. Please sign in again.',
      failed: 'Sign in failed',
      quickLoginEyebrow: 'QUICK LOGIN (TEST USERS)',
      quickLoginHint: 'Click to fill in — then press “Sign in”.',
      quickLoginAdmin: 'Admin',
      quickLoginEmployer: 'Employer',
      quickLoginProfessional: 'Professional',
      forgotLink: 'Forgot password?',
    },
    forgot: {
      eyebrow: 'Nº 03 — FORGOT PASSWORD',
      title: 'Forgot',
      titleAccent: 'password?',
      sub: 'Enter your email and we will send instructions to set a new password.',
      submit: 'Send reset email',
      loading: 'Sending …',
      successTitle: 'Check your email',
      successBody: 'If the account exists, we have sent an email with instructions. The link is valid for 30 minutes.',
      backToLogin: 'Remember your password? Sign in →',
      demoBannerTitle: 'DEMO MODE — email is not configured yet',
      demoBannerSub: 'In production this link would be sent by email.',
      demoBannerOpenLink: 'Open the reset link',
      demoBannerTokenLabel: 'Token',
    },
    reset: {
      eyebrow: 'Nº 04 — NEW PASSWORD',
      title: 'Set a',
      titleAccent: 'new password.',
      sub: 'Choose a new password of at least 8 characters.',
      newPassword: 'New password',
      confirmPassword: 'Confirm new password',
      submit: 'Reset password',
      loading: 'Updating …',
      mismatch: 'Passwords do not match.',
      tooShort: 'Password must be at least 8 characters.',
      successTitle: 'Password updated',
      successBody: 'Taking you to the sign in page in {seconds} seconds.',
      goToLogin: 'Go to sign in now →',
      invalidTitle: 'Link is invalid',
      reasonInvalid: 'This reset link is not valid.',
      reasonExpired: 'This reset link has expired. Request a new one.',
      reasonUsed: 'This reset link has already been used.',
      requestNew: 'Request a new reset link →',
    },
    signup: {
      eyebrow: 'Nº 02 — JOIN NORVIG',
      title: 'Create',
      titleAccent: 'account.',
      sub: 'Sign up in 30 seconds. Free to get started.',
      submit: 'Create account',
      loading: 'Creating account…',
      successToast: 'Welcome to Norvig.',
      failed: 'Registration failed',
      already: 'Already have an account?',
      goToLogin: 'Sign in →',
      googleHint: 'Want to use Google instead? Use “Continue with Google” on the sign in page.',
      duplicateEmail: 'An account already exists with this email. Click “Sign in” and try your password.',
      orgnrLabel: 'Institution’s organisation number',
      orgnrPlaceholder: '9 digits, e.g. 923609016',
      orgnrHint: 'The institution name is fetched automatically from the Norwegian Business Registry (BRREG).',
      institutionNotice: 'Please note: it is not the municipality as such that should be registered, but the specific institution — e.g. the nursing home, school or kindergarten — using the institution’s own organisation number.',
      brregLooking: 'Looking up the business registry…',
      brregCompanyLabel: 'Company (from BRREG)',
      brregNotFound: 'No organisation found with this number. Please check the organisation number.',
      brregRequired: 'A valid organisation number is required to register as an employer.',
      switchToEmployer: 'Representing a municipality? Register it here',
      switchToProfessional: 'Are you a skilled worker? Register here',
    },
  },
  hero: {
    title: { line1: 'Norway awaits.', line2: 'Those who care.' },
    subtitle:
      'Norvig connects nurses, care workers and other health professionals directly with Norwegian employers. No middlemen. Full support — from first application to first shift.',
    ctaPrimary: 'Sign up',
    ctaSecondary: 'How it works',
    trust: ['Free for skilled workers', 'Safe and quality-assured', 'Precise matching'],
    imageAlt: 'Premium recruitment platform for Norway',
    scrollHint: 'Scroll for more',
  },
  heroCinematic: {
    eyebrow: 'Nº 01 — NORVIG TALENTS',
    headline: 'Create your new life in Norway.',
    lede:
      'Norvig is a modern staffing platform connecting health professionals and educators with employers who value care, competence and reliability.',
    ctaEmployers: 'For employers',
    ctaWorkers: 'For workers',
    scrollHint: 'Scroll on',
    imageAlt: 'Norwegian fjord at dawn — waves and light over the sea.',
  },
  sectors: {
    eyebrow: 'Sectors',
    titleA: 'One platform.',
    titleB: 'Three sectors.',
    sub:
      'Health and care is the core of Norvig — from nursing homes and home care to health centres. We also cover education and childcare, plus seasonal work in fruit, berries and tourism. Direct, without middlemen.',
    areasOne: 'area',
    areasMany: 'areas',
    explore: 'Explore',
    openings: 'open',
    openingsComingSoon: 'Coming soon',
    ctaExploreAll: 'Explore all sectors',
  },
  journey: {
    eyebrow: 'The journey through Norway',
    titleA: 'From first thought',
    titleB: 'to first day.',
    sub:
      'We walk the road together — from first application to settled life in Norway. Clear, safe and without hidden costs.',
    ctaStart: 'Start your journey',
    steps: [
      { place: 'From home',          title: 'Match',        desc: 'Build your profile. We find the roles that match your skills.' },
      { place: 'Online',             title: 'Interview',    desc: 'We prepare you for the interview and help with the contract.' },
      { place: 'Health Directorate', title: 'Authorisation', desc: 'We guide you through authorisation and visa application.' },
      { place: 'OSL · BGO · TRD',    title: 'Settling in',  desc: 'Housing, banking, language courses and the everyday — we sort it together.' },
      { place: 'Home in Norway',     title: 'Established',  desc: 'We stay close — long after you’ve started the job.' },
    ],
  },
  stats: {
    // Soft, documentable product facts — shown until real platform numbers
    // pass the per-metric thresholds in Stats.jsx (fetched live from
    // /api/public/platform-stats).
    items: [
      { value: '1,390+', label: 'Health & care institutions mapped' },
      { value: '0 NOK',  label: 'Free for skilled workers' },
      { value: '100%',   label: 'BRREG-verified employers' },
      { value: 'Direct', label: 'Contact without middlemen' },
    ],
    live: {
      jobs: 'Active openings',
      professionals: 'Registered workers',
      employers: 'Employers',
    },
    liveBadge: 'Live platform numbers — updated now',
  },
  kontakt: {
    eyebrow: 'Contact',
    title: 'Talk to us',
    titleAccent: 'directly.',
    sub: 'Are you a municipality or employer considering Norvig? Send us a message — we usually reply within one business day.',
    form: {
      name: 'Name',
      email: 'Email',
      organization: 'Organisation (optional)',
      phone: 'Phone (optional)',
      category: 'I represent',
      categories: {
        kommune: 'Municipality',
        virksomhet: 'Public organisation',
        fagarbeider: 'Skilled worker',
        annet: 'Other',
      },
      message: 'Message',
      messagePlaceholder: 'Briefly tell us what it concerns …',
      submit: 'Send message',
      sending: 'Sending …',
      successTitle: 'Thank you!',
      successBody: 'We have received your inquiry and usually reply within one business day.',
      sendAnother: 'Send another message',
      errorGeneric: 'Something went wrong. Please try again shortly.',
      errorRate: 'Too many messages in a short time — try again in fifteen minutes.',
    },
    aside: {
      title: 'Other ways in',
      items: [
        { label: 'For employers', desc: 'Read how the platform works for you.', href: '/for-kommuner' },
        { label: 'Pricing', desc: 'Open sector rates — published in full.', href: '/priser' },
      ],
    },
  },
  sectorPage: {
    eyebrow: 'NORVIG · SECTOR',
    statsJobs: 'Open positions',
    statsCandidates: 'Registered candidates',
    ctaCandidate: 'Sign up as a candidate',
    ctaEmployer: 'For employers',
    salaryTitle: 'Professions and indicative pay',
    salarySub: 'Gross annual salary in the public sector. Actual pay depends on education, seniority and workplace.',
    salarySource: 'Indicative ranges based on public wage statistics (SSB) and current collective agreements (KS).',
    perYear: 'NOK/yr',
    benefitsTitle: 'What you can expect',
    teasersTitle: 'Candidates in this sector',
    teasersSub: 'An anonymised selection of registered professionals.',
    teasersEmpty: 'No candidate cards to show yet.',
    teaserYears: (n) => `${n} years of experience`,
    teaserAvailable: 'Available',
    teaserCta: 'Register your organisation to see full profiles',
    share: 'Copy share link',
    shared: 'Link copied',
  },
  kommunePage: {
    nav: { login: 'Log in', signup: 'Register your institution' },
    active: {
      eyebrow: 'Active on Norvig',
      title: 'Municipalities already',
      titleAccent: 'recruiting directly.',
      body: 'This overview is data-driven and updates automatically as new positions are published on Norvig.',
      jobOne: 'open position',
      jobMany: 'open positions',
      empty: 'The first municipalities are on their way. Be among the first —',
      emptyCta: 'register your municipality for free',
    },
    hero: {
      eyebrow: 'For Norwegian municipalities',
      title: 'Staff health & care',
      titleAccent: 'without middlemen.',
      sub: 'Norvig connects your municipality directly to qualified health professionals — nurses, care workers and social educators — with verified profiles, document handling and an open pricing model. Free to get started.',
      cta: 'Register your institution',
      pricingLink: 'See the pricing model',
      demoCta: 'Book a demo',
    },
    demo: {
      eyebrow: 'Book a demo',
      title: 'See Norvig in practice —',
      titleAccent: 'pick a time that suits you.',
      body: 'A 30-minute video call where we show how your municipality can find, assess and hire health professionals directly. Free and non-binding.',
      dateLabel: 'Pick a date',
      slotLabel: 'Pick a time',
      slotHint: 'All times are Norwegian time. Weekdays 09–15.',
      pickDateFirst: 'Pick a date in the calendar first.',
      allTaken: 'No available times this day — pick another date.',
      form: {
        name: 'Name',
        email: 'Email',
        organization: 'Municipality',
        phone: 'Phone (optional)',
        note: 'What would you like to see? (optional)',
      },
      submit: 'Book demo call',
      submitting: 'Booking …',
      successTitle: 'Demo booked!',
      successBody: 'We have received your booking for {date} at {slot}. You will get a confirmation email with a calendar invite (.ics) and the meeting link — the call is ready, no extra steps.',
      meetingLinkLabel: 'Meeting link:',
      addToCalendar: 'Add to calendar',
      newBooking: 'Book another time',
      error: 'Something went wrong. Try again — or write to hei@norvig.no.',
      slotTaken: 'That time was just taken. Pick another.',
    },
    valuesEyebrow: 'Why Norvig',
    values: [
      { title: 'Direct contact', body: 'You talk to the candidates yourselves — no agency or middlemen. Messages, documents and agreements in one place.' },
      { title: 'Verified profiles', body: 'Candidates can upload CVs, authorisations and diplomas. Employers are BRREG-verified against the national registry at onboarding.' },
      { title: 'International talent pool', body: 'Nurses, care workers and educators from the EEA and beyond — ready for Norwegian employers.' },
      { title: 'Open pricing', body: 'Free to register and search. Sector rates on hire are published openly — no hidden fees or lock-in.' },
    ],
    calc: {
      sectionEyebrow: 'The cost picture',
      sectionTitle: 'See what the middleman',
      sectionTitleAccent: 'actually costs.',
      sectionBody: 'Traditional agency recruitment is typically priced as a share of annual salary. With Norvig you search for free, talk directly to candidates, and pay only on an actual hire — at open sector rates.',
      bullets: ['Free registration and candidate search', 'No lock-in or subscription', 'Published rates per sector'],
      eyebrow: 'Example calculation',
      title: 'What does the agency markup cost?',
      sub: 'Recruitment agencies typically charge 15–25% of annual salary per hire. Calculate what that means for you — and compare with Norvig\u2019s published sector rates.',
      countLabel: 'Number of hires',
      salaryLabel: 'Annual salary per position',
      resultLabel: 'Typical agency cost (15–25% of salary)',
      disclaimer: 'Example calculation — not a quote. Norvig\u2019s actual rates per sector are published openly on the pricing page, and registration and candidate search are free.',
    },
    processEyebrow: 'How it works',
    steps: [
      { title: 'Register the municipality', body: 'Create an account and verify the organisation number against BRREG.' },
      { title: 'Post positions', body: 'Describe the need — the platform matches against relevant candidates in your sector.' },
      { title: 'Review candidates', body: 'See profiles, CVs and documents. AI-assisted matching helps you prioritise.' },
      { title: 'Hire directly', body: 'Arrange interviews and hiring directly with the candidate. We support the process all the way.' },
    ],
    cta: {
      title: 'Ready to meet',
      titleAccent: 'the candidates directly?',
      body: 'Create a municipality account in minutes. Questions first? We are happy to answer.',
      signup: 'Register your institution',
      contact: 'Contact us',
    },
  },
  norgeDream: {
    eyebrow: 'The dream of Norway',
    titleA: 'More than a job.',
    titleB: 'A life.',
    sub:
      'Norway may not be the first place you imagine. But those who come here tell the same story: a country where everyday life feels human — and where there is still room to breathe.',
    imageAlt: 'Rural Norway — red houses by the fjord',
    sectionNumber: 'Nº 02',
    sectionTag: 'Life in Norway',
    galleryTagline: 'A society where the mountain is a neighbour and the summer light lasts all evening.',
    gallery: [
      { id: 'landscape-nature',  caption: 'Landscape & nature',     src: '/hero/landscape-nature.webp',  alt: 'Norwegian landscape and nature' },
      { id: 'idyllic-community', caption: 'Idyllic communities',    src: '/hero/idyllic-community.webp', alt: 'Idyllic Norwegian community' },
      { id: 'picturesque-town',  caption: 'Picturesque towns',      src: '/hero/picturesque-town.webp',  alt: 'Picturesque Norwegian town' },
      { id: 'sport-leisure',     caption: 'Sport & leisure',        src: '/hero/sport-leisure.webp',     alt: 'Sport and leisure in Norway' },
      { id: 'culture-life',      caption: 'Culinary',               src: '/hero/culture-life.webp',      alt: 'Nordic gastronomy' },
      { id: 'fjord-village',     caption: 'Rural Norway',           src: 'https://images.unsplash.com/photo-1564739749969-e85beb154a32?auto=format&fit=crop&w=1600&q=85', alt: 'Rural Norway — red houses by the fjord' },
    ],
    norgeITall: {
      eyebrow: 'Norway in numbers',
      items: [
        { n: '103,000', u: 'km',   l: 'coastline' },
        { n: '1,190',   u: '',     l: 'fjords' },
        { n: '240,000', u: '',     l: 'lakes' },
        { n: '5.6',     u: 'mill', l: 'inhabitants' },
      ],
    },
    hverdagen: {
      eyebrow: 'Everyday life',
      title: 'A life here means something concrete.',
      fakta: [
        { n: '49',    u: 'weeks',   l: 'parental leave',  d: 'paid in full — for both parents.' },
        { n: '25',    u: 'days',    l: 'holiday per year', d: 'minimum. Many have more.' },
        { n: '7.5',   u: 'h / day', l: 'working hours',   d: 'and home for dinner.' },
        { n: 'Top 10', u: '',        l: 'happiest country', d: 'year after year, according to the UN World Happiness Report.' },
      ],
    },
    pullquote:
      'Here you have the right to paid leave when your child is sick. After work the mountains, the sea or the forest are never far away. In Norway you meet a society built on trust and community.',
    quoteAuthor: '— Everyday life in Norway',
    lightbox: {
      openHint: 'Click to expand',
      openLabel: 'Open {caption} in fullscreen',
      close: 'Close fullscreen image',
      prev: 'Previous image',
      next: 'Next image',
      share: 'Share image',
      shareTitle: 'Norvig — NorgeDream',
      shareText: 'Check out this image from Norvig\u2019s NorgeDream gallery',
      shareCopied: 'Link copied',
      shareFailed: 'Could not share',
    },
  },
  tenders: {
    nav: 'Tenders',
    public: {
      pageEyebrow: 'NORVIG · TENDERS',
      title: 'Tenders Norvig monitors',
      italicSuffix: 'recruitment, health & education.',
      lede: 'We monitor relevant Norwegian public-sector tenders on Doffin across recruitment, health and education. Here is what is currently active.',
      cpvHeading: 'Categories we track',
      filterLabel: 'Sector',
      filterAll: 'All',
      sectorHelse: 'Health',
      sectorUtdanning: 'Education',
      sectorGenerell: 'General recruitment',
      sortLabel: 'Sort',
      sortNewest: 'Newest first',
      sortDeadline: 'Closest deadline',
      cardPublished: 'Published',
      cardDeadline: 'Deadline',
      cardDeadlineSoon: 'Deadline in {n} days',
      cardDeadlinePassed: 'Deadline passed',
      cardValue: 'Est. value',
      cardViewDoffin: 'View on Doffin',
      empty: 'No active tenders right now. We check daily — please return later.',
      lastFetched: 'Source: Doffin · last updated {dt}',
      mercellHeading: 'Other sources we follow',
      mercellBody: 'In addition to Doffin we follow Mercell and TED (Tenders Electronic Daily) for European tenders. Search directly:',
      mercellLink: 'Search Mercell',
      tedLink: 'Search TED Europe',
      pagination: '{from}–{to} of {total}',
    },
    status: {
      new: 'New',
      reviewed: 'Reviewed',
      interested: 'Interested',
      bid_submitted: 'Bid submitted',
      won: 'Won',
      lost: 'Lost',
      not_relevant: 'Not relevant',
      archived: 'Archived',
    },
    sector: {
      helse: 'Health',
      utdanning: 'Education',
      generell: 'General',
    },
    cta: {
      interested: 'Norvig is following this opportunity — interested in partnering?',
      bidSubmitted: 'Norvig has submitted a bid — let\'s talk',
      contactButton: 'Get in touch',
      highValueBadge: 'High-value',
    },
    inquiry: {
      modal: {
        eyebrow: 'CONTACT NORVIG',
        title: 'Get in touch about this tender',
        subtitle: 'We respond within 24 hours.',
        nameLabel: 'Name',
        companyLabel: 'Company',
        emailLabel: 'Email',
        phoneLabel: 'Phone (optional)',
        messageLabel: 'Message',
        defaultMessage: "I am interested in Norvig's work on the tender '{title}' (Doffin ref: {ref}). Please get in touch for a conversation.",
        send: 'Send inquiry',
        sending: 'Sending …',
        cancel: 'Cancel',
        successTitle: 'Thank you!',
        successBody: 'We will get back to you within 24 hours.',
        errorGeneric: 'Something went wrong. Please try again.',
        errorRate: 'Too many inquiries from this IP. Please try again in an hour.',
        emailInvalid: 'Please enter a valid email address.',
        required: 'Required',
      },
    },
  },
  documents: {
    eyebrow: 'DOCUMENTS',
    cvHeading: 'CV',
    cvHelper: 'PDF, max 10 MB',
    cvUploadCta: 'Drop PDF here or click to choose',
    cvPreview: 'Preview',
    cvDelete: 'Delete CV',
    cvBadge: 'CV',
    certHeading: 'Certificates',
    certCount: '{n} of {max}',
    certEmpty: 'No certificates yet.',
    certAddCta: 'Add certificate',
    certAddModalTitle: 'New certificate',
    certTitleLabel: 'Title',
    certTitlePlaceholder: 'e.g. Nursing licence',
    certFileLabel: 'PDF file',
    certAddSubmit: 'Save certificate',
    certUploading: 'Uploading …',
    certPreview: 'Preview',
    certDelete: 'Delete',
    certFull: 'You have reached the limit of {max} certificates. Delete one to add another.',
    cancelLabel: 'Cancel',
    inlinePreview: {
      heading: 'Preview',
      loading: 'Loading preview …',
      error: 'Could not load the preview. Try downloading instead.',
      unavailableDocx: 'Preview is not available for DOCX files. Download to open the document.',
      close: 'Close preview',
      download: 'Download',
    },
    pdf: {
      close: 'Close',
      download: 'Download',
      prev: 'Previous page',
      next: 'Next page',
      page: 'Page',
      of: 'of',
      loading: 'Loading …',
      error: 'Could not load PDF.',
    },
    candidateCardCvBadge: 'CV',
    candidateCardCertBadge: '{n} certificate',
    candidateCardCertBadgePlural: '{n} certificates',
    employerOnlyMessage: 'Requires employer account',
    proDocs: {
      eyebrow: 'MY DOCUMENTS',
      heading: 'My documents',
      subtitle: 'Upload your CV, certificates and other relevant documents (PDF, DOCX, JPEG, PNG — max 10 MB per file, 10 total).',
      uploadCta: 'Upload new',
      empty: 'No documents yet. Upload your CV to get started.',
      counter: '{n} of {max} documents',
      categories: {
        cv: 'CV',
        sertifikat: 'Certificate',
        vitnemal: 'Diploma',
        anbefaling: 'Recommendation',
        annet: 'Other',
      },
      actions: {
        preview: 'Preview',
        download: 'Download',
        edit: 'Edit',
        delete: 'Delete',
      },
      deleteConfirm: 'Are you sure you want to delete this document?',
      upload: {
        modalTitle: 'Upload document',
        modalSubtitle: 'Allowed: PDF, DOCX, JPEG, PNG — max 10 MB.',
        dropzone: 'Drop your file here',
        dropzoneSub: 'or click to choose',
        selectFile: 'Choose file',
        chosenFile: 'Selected:',
        categoryLabel: 'Category',
        categoryPlaceholder: 'Choose category …',
        customLabelLabel: 'Short label',
        customLabelHelp: 'Max 60 characters',
        descriptionLabel: 'Note (optional)',
        descriptionHelp: 'Max 280 characters — visible to employers with access',
        confirmButton: 'Upload',
        uploading: 'Uploading …',
        cancel: 'Cancel',
        errors: {
          tooLarge: 'File is too large. Max 10 MB.',
          invalidType: 'Invalid file type. Allowed: PDF, DOCX, JPEG, PNG.',
          magicMismatch: 'File content does not match the file type.',
          limitReached: 'You have reached the limit of 10 active documents.',
          rateLimited: 'You have hit the daily upload limit (10). Try again tomorrow.',
          generic: 'Upload failed. Please try again.',
          emptyFile: 'Empty file.',
        },
        success: 'Document uploaded.',
      },
      edit: {
        modalTitle: 'Edit document',
        saveButton: 'Save changes',
        saved: 'Changes saved.',
      },
      employerView: {
        eyebrow: 'CANDIDATE DOCUMENTS',
        heading: 'Documents',
        empty: 'This candidate has not shared any documents yet.',
        accessHint: 'You have access because of mutual engagement (saved candidate, conversation, or message).',
        download: 'Download',
        requestAccessCta: 'Request CV access',
        requestAccessHint: 'You do not have access to this candidate\'s documents yet. The candidate is notified by message when you request access.',
        requestAccessFailed: 'Could not request access. Try again.',
      },
      preferences: {
        eyebrow: 'NOTIFICATIONS',
        heading: 'Notifications',
        subtitle: 'Get notified when relevant things happen on your profile.',
        documentAccess: 'Email me when an employer reviews my documents',
        documentAccessHint: 'Max one email per employer per 24 hours. We never reveal the employer\'s name — only the sector.',
        saved: 'Saved',
        saving: 'Saving …',
      },
    },
  },
  features: {
    eyebrow: 'Why Norvig',
    titleA: 'Built on precision.',
    titleB: 'Driven by people.',
    items: [
      { title: 'Precise matching',         desc: 'Norvig weighs experience, certification, language and preferences. The right candidate for the right role — in seconds.' },
      { title: 'Work where you\'re needed most', desc: 'Nursing homes, home care, health centres and care housing — plus schools and kindergartens — across the whole country.' },
      { title: 'Direct contact',           desc: 'Speak with the employer yourself. No middlemen. No fees.' },
      { title: 'Get to know Norway',       desc: 'Profiles of municipalities, housing, culture and daily life.' },
      { title: 'Safe and quality-assured', desc: 'We verify everyone. Your data stays secure. Fully GDPR-compliant.' },
    ],
  },
  howItWorks: {
    eyebrow: 'How it works',
    titleA: 'Simple to start.',
    titleB: 'Safe to finish.',
    cols: [
      {
        kind: 'pro',
        title: 'For you looking for work',
        cta: 'Get started for free',
        steps: [
          { title: 'Build your profile', desc: 'Tell us about your skills, experience and what you’re looking for.' },
          { title: 'Get matches',        desc: 'Receive roles that fit your skills — not just keywords.' },
          { title: 'Apply and begin',    desc: 'Send your application — we’re with you all the way to Norway.' },
        ],
      },
      {
        kind: 'emp',
        title: 'For you hiring',
        cta: 'Start recruiting',
        steps: [
          { title: 'Create an account',     desc: 'Register your municipality or company — free to get started.' },
          { title: 'Post your role',        desc: 'Reach hundreds of qualified applicants in short time.' },
          { title: 'Choose your candidate', desc: 'Get precise matches and speak with them directly.' },
        ],
      },
    ],
  },
  whyNorway: {
    eyebrow: 'Why Norway',
    titleA: 'A country worth',
    titleB: 'staying in.',
    sub: 'Scandinavian quality of life, breathtaking nature and a world-class healthcare system. The kind of life many only dream of.',
    items: [
      { title: 'Wages that matter',     desc: 'Collective agreements and good conditions — throughout your career.' },
      { title: 'Everyday balance',      desc: 'Generous holidays, room for family and time to live.' },
      { title: 'Real safety',           desc: 'Health and welfare that hold you from day one.' },
      { title: 'An open society',       desc: 'Inclusive, international and equal working life.' },
    ],
  },
  success: {
    eyebrow: 'Story',
    irrisHeadline: '“It is possible to begin again. It is possible to find your place.”',
    irrisBody1:
      'Irris Hollanes Lua came to Norway from the Philippines with a single visa and not a word of Norwegian. Today she works as a nurse at Helsefonna in Haugesund — after learning the language, having her education recognised and building herself up from a healthcare worker in Finnmark.',
    irrisBody2Pre: 'The road went through long journeys, demanding exams and sleepless nights. ',
    irrisBody2Bold: 'But she did not give up.',
    irrisBody2Post:
      ' Step by step she built herself up — from Finnmark, to Vindafjord, to Haugesund. Today she is active in multicultural work and integration.',
    irrisBullets: [
      'From visitor visa to authorised nurse — without shortcuts',
      'Norwegian language exams, Health Directorate authorisation, on-the-job training',
      'Today active in multicultural work and integration',
    ],
    irrisCardEyebrow: 'From the Philippines to Norway',
    irrisCardName: 'Irris Hollanes Lua',
    irrisCardRole: 'Nurse · Helsefonna, Haugesund',
    irrisPortraitAlt: 'Irris Hollanes Lua — nurse at Helsefonna',
    ctaRead: 'Read the full story',
    ctaJoin: 'Become the next story',
    moreEyebrow: 'More stories',
    moreTitle: 'More who have found their place.',
    moreSeeAll: 'See all 6 stories',
    cardCtaBadge: 'Your turn',
    cardCtaTitle: 'The next story could be yours.',
    cardCtaSub: 'Norvig walks with you from first application to first day on the job — and long after.',
    cardCtaButton: 'Start your journey',
  },
  testimonials: {
    eyebrow: 'Voices',
    titleA: 'From skilled workers',
    titleB: 'and employers.',
    items: [
      {
        name: 'Lars Andersen',
        role: 'HR manager',
        org: 'Bergen Municipality',
        text: 'We found perfect candidates in just a few weeks. The matching saves us a huge amount of time.',
      },
      {
        name: 'Klaudia Pawlowski',
        role: 'Nurse',
        org: 'Stavanger',
        text: 'From Poland to Norway in 8 weeks. Norvig made the paperwork easier than I thought possible.',
      },
      {
        name: 'Jens Eriksen',
        role: 'Department head, nursing home',
        org: 'Tromsø',
        text: 'We’ve hired four people through Norvig over the past year. The quality of the candidates is exceptional.',
      },
    ],
  },
  faq: {
    eyebrow: 'Questions',
    titleA: 'Frequently asked',
    titleB: 'questions.',
    sub: 'Most things you’ll find answered here. Still wondering about something? Let us know — we usually reply the same day.',
    items: [
      { q: 'Does Norvig cost anything?',           a: 'For skilled workers, Norvig is completely free. Employers register and search for free, and only pay a published sector rate on an actual hire — see the pricing page. No subscriptions or hidden costs.' },
      { q: 'How does the matching work?',          a: 'We weigh experience, skills, language and what you’re looking for — and connect you with the roles that actually fit.' },
      { q: 'What kind of roles will I find?',      a: 'First and foremost health and care: nurses, health care workers, social educators and more — in nursing homes, home care and health centres. Education and childcare as well. Across the whole country.' },
      { q: 'What happens to my information?',     a: 'Privacy is at the centre. All data is stored securely and encrypted. We follow GDPR and Norwegian privacy law — strictly.' },
      { q: 'Can I apply for several roles?',       a: 'Yes. Apply for as many as you like and follow the status of every application from your own dashboard.' },
      { q: 'Do you help with relocation?',         a: 'Yes. You get access to municipality profiles, housing, transport and everyday life — and human support all the way.' },
    ],
  },
  finalCta: {
    titleA: 'Ready for a',
    titleB: 'new chapter?',
    sub:
      'Join us — alongside thousands of skilled workers and employers who have already found each other on Norvig. It takes less than a minute.',
    ctaPrimary: 'Sign up for free',
    ctaSecondary: 'See open roles',
    footnote: 'No hidden costs · Cancel anytime · GDPR-compliant',
  },
  footer: {
    banner: {
      eyebrow: 'Norvig — Norway’s recruitment platform',
      titleA: 'Norway’s platform for direct',
      titleB: 'connection between talent and employer.',
      sub: 'Staffing for health & care — and education. From first conversation to signed contract — without middlemen and without fees.',
    },
    hq: {
      eyebrow: 'Norvig HQ',
      location: 'Norway',
    },
    brandBlurb: 'Norvig connects health professionals and educators directly with Norwegian employers — without middlemen. From nursing homes to schools. All of Norway.',
    // ─── Facelift Phase 1 — editorial manifest (2-line, short) ───────────
    manifest: 'The staffing platform connecting the world\'s health professionals with the Norwegian employers who need them.',
    // ─── Facelift Phase 1 — editorial column groups ──────────────────────
    editorialCols: [
      {
        title: 'For professionals',
        links: [
          { label: 'Sign up',           href: '/signup' },
          { label: 'Browse jobs',       href: '/jobs' },
          { label: 'Sectors',           href: '/#sektorer', scroll: 'sektorer' },
          { label: 'How it works',      href: '/#how-it-works', scroll: 'how-it-works' },
          { label: 'Resources',         href: '/horisont' },
        ],
      },
      {
        title: 'For employers',
        links: [
          { label: 'Get started',       href: '/for-kommuner' },
          { label: 'Post a job',        href: '/signup' },
          { label: 'Pricing',           href: '/priser' },
          { label: 'Tenders',           href: '/anbud' },
          { label: 'Case studies',      href: '/historier' },
        ],
      },
      {
        title: 'Company',
        links: [
          { label: 'About us',          href: '/om-oss' },
          { label: 'About Norway',      href: '/om-norge' },
          { label: 'Municipality guide', href: '/kommuneoversikt' },
          { label: 'Newsletter',        href: '/nyhetsbrev' },
          { label: 'Investors',         href: '/investors' },
          { label: 'Contact',           href: '/kontakt' },
        ],
      },
      {
        title: 'Legal',
        links: [
          { label: 'Terms',             href: '/vilkar/fagarbeider' },
          { label: 'Privacy',           href: '/privacy' },
          { label: 'Cookies',           href: '/privacy#cookies' },
        ],
      },
    ],
    // ─── Bottom bar ──────────────────────────────────────────────────────
    bottomCopyright: '© 2026 Norvig',
    rightsReserved: 'All rights reserved',
    cols: [],
    stats: [
      { n: '5.6M', l: 'Inhabitants' },
      { n: '357',  l: 'Municipalities' },
      { n: '2',    l: 'Sectors' },
      { n: '1',    l: 'Platform' },
    ],
    copyright: '© 2026 Norvig AS · Org. 931 928 732 · Norway',
    tagline: 'Built with care in Norway.',
    miljofyrtarn: {
      label: 'Eco-Lighthouse Certified',
      cta: 'Learn more',
      alt: 'Eco-Lighthouse — certified business',
      ariaLabel: 'Norvig is Eco-Lighthouse certified — learn more (opens in new tab)',
    },
    miniCopyright: '© 2026 — Norway’s platform for direct connection.',
    miniLinks: [
      { label: 'Home',         href: '/' },
      { label: 'Stories',      href: '/historier' },
      { label: 'Sectors',      href: '/#sektorer', scroll: 'sektorer' },
      { label: 'About Norway', href: '/om-norge' },
      { label: 'About us',     href: '/om-oss' },
    ],
  },
  omNorge: {
    eyebrow: 'A life in Norway',
    titleA: 'A country worth',
    titleB: 'staying in.',
    sub: 'Norway is more than a workplace. It is safety, balance and freedom — built on trust. A society where everyday life is human, and where there is still room to breathe.',
    imageAlt: 'Kristiansand harbour at blue hour',
    factsEyebrow: 'Everyday life',
    factsTitle: 'Four things that make Norway different.',
    facts: [
      { label: 'Wages',       title: 'Tariff from day one',     text: 'Norwegian collective agreements guarantee predictable pay. Overtime is paid. Trust in your employer’s honesty is built into the system.' },
      { label: 'Balance',     title: '7.5 hours per day',       text: 'The standard workday is 7.5 hours. 25 days of holiday. 49 weeks of paid parental leave. Family time is not a privilege — it is the norm.' },
      { label: 'Safety',      title: 'Among the world’s safest',      text: 'Norway is ranked among the world’s safest countries. Low crime. Reliable public services. A healthcare system that works.' },
      { label: 'Openness',    title: 'Trust as default',        text: 'The boss takes the bus. The Prime Minister does their own shopping. Hierarchies are flat, communication direct. Trust is a social contract.' },
    ],
    statsEyebrow: 'Norway in numbers',
    statsTitle: 'The numbers that tell of daily life.',
    stats: [
      { v: '49 weeks',   l: 'Parental leave, paid' },
      { v: '25 days',    l: 'Holiday, by law' },
      { v: '7.5 h/day',  l: 'Standard workday' },
      { v: 'Top 10',     l: 'Safest country in the world' },
    ],
    quote: 'The sun does not set. It only rests a while — like a pause in the middle of summer.',
    quoteAuthor: '— Everyday life in Norway',
    ctaTitle: 'Ready for a new chapter?',
    ctaSub: 'Norvig connects skilled workers directly with Norwegian employers. No middlemen. Full support, from first conversation to settling in.',
    ctaPrimary: 'Sign up',
    ctaSecondary: 'Read stories',
  },
  omOss: {
    eyebrow: 'The people behind Norvig',
    titleA: 'Built by people who',
    titleB: 'know the journey.',
    sub: 'Norvig is run by a Norwegian team with experience from recruitment, integration and business. We are convinced that skilled workers deserve direct contact — and that employers deserve quality without middlemen. Together we build a platform that makes it easier to find one another.',
    teamEyebrow: 'The team',
    teamTitle: 'The people behind it.',
    team: [
      // MOVED to DB: `team_portraits` collection. Consumed via
      // usePublicTeamPortraits() → /api/team-portraits. Edit at runtime
      // through /admin/team-portraits UI. Fallback: config/team-portraits.js.
    ],
    companyEyebrow: 'A journey through healthcare — From Albania to Norway',
    partnership: {
      heading: 'The road to Norvig.',
      lede: 'A journey through healthcare — from Shkodër to Hardanger — that became the foundation for Norvig.',
      kastriot: {
        name: 'Kastriot Prenga',
        location: 'Hardanger, Norway',
        photoAlt: 'Kastriot Prenga, founder of Norvig',
      },
      quote: {
        text: 'We’re not just building a platform. We’re building a better way to connect people with work.',
        attribution: '— Kastriot Prenga',
      },
    },
    career: {
      eyebrow: 'CAREER',
      heading: 'A journey through healthcare — from Shkodër in Albania to Hardanger in Norway — that became the foundation of Norvig.',
      sub: 'Eighteen years across healthcare, leadership and professional advocacy — from Shkodër to Hardanger.',
      periods: [
        {
          label: 'Albania — education and leadership',
          years: '2007–2011',
          entries: [
            { year: '2007', icon: 'GraduationCap', text: 'Graduated as a nurse from the University of Shkodër, Albania (age 21).' },
            { year: '2007–2009', icon: 'HeartHandshake', text: 'Coordinator and interpreter for Italian Caritas in Vau-Dejes, Albania.' },
            { year: '2008–2009', icon: 'BookOpen', text: 'Studies in law (one year).' },
            { year: '2009', icon: 'Stethoscope', text: "Employed as a nurse at a health centre in Albania with responsibility for the doctor's office, public health clinic and physiotherapy services. Responsible for operations, finances and staff." },
            { year: '2010', icon: 'Users', text: 'Appointed senior nurse / department head — the youngest in such a leadership role at the health centre in Helse-Nord Albania.' },
            { year: '2011', icon: 'Briefcase', text: 'Acting director (operations manager) at the health centre, appointed by the regional health director of Northern Albania. Held the role for six months.' },
            { year: 'Dec. 2011', icon: 'Plane', text: 'Moved to Norway to continue his career in healthcare and leadership.' },
          ],
        },
        {
          label: 'Norway — clinical career',
          years: '2012–2017',
          entries: [
            { year: '2012', icon: 'HeartPulse', text: 'Began his career in Norway as a care worker in the municipal health service in Hardanger.' },
            { year: '2013', icon: 'Award', text: 'Obtained Norwegian authorisation as a nurse and took up the role of nurse in the municipality.' },
            { year: '2013–2017', icon: 'TrendingUp', text: 'Completed numerous courses and qualifications in dementia care, laboratory work, regulations, and quality and emergency preparedness.' },
          ],
        },
        {
          label: 'Union representative and leadership',
          years: '2017–2023',
          entries: [
            { year: '2017', icon: 'Handshake', text: 'Elected chief shop steward for the Norwegian Nurses Organisation (NSF), responsible for pay negotiations, shift arrangements, agreements and partnership cooperation.' },
            { year: '2019', icon: 'Vote', text: "Elected delegate to NSF's national congress. Submitted three motions, two of which were adopted by majority." },
            { year: '2019–2023', icon: 'UserCog', text: 'Elected to the county board of NSF Vestland, actively contributing to organisational development and healthcare policy.' },
            { year: '2019–2020', icon: 'Building2', text: 'Participated in several working groups on the municipal merger reform, collaborating closely with politicians, administration and professional networks.' },
            { year: '2020', icon: 'GraduationCap', text: 'Completed further education in healthcare leadership and competence development.' },
            { year: '2020', icon: 'ClipboardList', text: 'Team lead in coordinated healthcare services in the municipality, responsible for professional development, quality and service coordination.' },
          ],
        },
        {
          label: 'Founder',
          years: '2023–',
          entries: [
            { year: '2023', icon: 'Rocket', text: 'Founded a company aimed at addressing the healthcare staffing shortage through recruitment to permanent positions, systems understanding, and innovative solutions rooted in healthcare and public administration experience.' },
            { year: '2026', icon: 'Sparkles', text: 'Leads the development of an interdisciplinary concept for recruitment and competence delivery, in collaboration with experts in healthcare, organisational development, economics and artificial intelligence.' },
          ],
        },
      ],
    },
    founderJourney: {
      attribution: '— KASTRIOT PRENGA',
      paragraphs: [
        'With a background in healthcare in both Albania and Norway, I am well equipped to contribute to solutions that benefit healthcare professionals and clients alike.',
        'After Kastriot moved to Norway, he has worked as a healthcare worker, nurse and team leader across hospitals, nursing homes and home care. This broad experience has given him a holistic perspective on the challenges and needs of the healthcare system, and has enabled him to identify what matters to both employers and employees.',
        'As chief shop steward and county board representative in the Norwegian Nurses Association Vestland, Kastriot has fought for better working conditions, health-promoting shift schedules and competitive pay. He has seen up close how healthcare professionals can feel overworked and undervalued, and is therefore dedicated to finding solutions that meet their needs while also addressing the financial challenges facing health authorities and municipalities.',
        'Through his experience with staffing agencies, he has gained a thorough understanding of how to ensure satisfaction and engagement among both healthcare professionals and clients.',
        'Now Kastriot is focused on using this experience to build a recruitment and staffing agency that is innovative and cost-effective, while delivering highly qualified healthcare professionals at a lower price. His ambition is to contribute to a sustainable solution to the nursing shortage by offering high quality to the healthcare sector, while giving healthcare professionals better pay and working conditions.',
      ],
      pullQuote: 'Norvig is built by someone who has walked the road — and who knows what actually works.',
    },
    ctaTitle: 'Want to get to know us?',
    ctaSub: 'We’re happy to answer questions about the platform, partnership or careers at Norvig.',
    ctaPrimary: 'Contact the team',
    ctaSecondary: 'Read stories',
    investors: {
      eyebrow: 'Investor relations',
      title: 'For investors.',
      body: 'Norvig is exploring investors for the scaling phase. The material is confidential and password protected.',
      cta: 'Investor sign in',
    },
  },
  historier: {
    pageEyebrow: '—— Stories',
    pageTitleA: 'Real people.',
    pageTitleB: 'Real journeys.',
    pageSub: 'Behind every number in the statistics is a person who dared to begin again. Here are some of the journeys that have shaped Norvig.',
    storyEyebrow: 'Story',
    storyEntryLabel: 'Story',
    ctaRead: 'Read the full story',
    ctaBack: 'Back to all stories',
    ctaJoinNext: 'Your story could be the next one.',
    ctaJoinNextSub: 'Norvig walks with you from first application to first day on the job — and long after.',
    ctaJoinNextPrimary: 'Start your journey',
    ctaJoinNextSecondary: 'Back to home',
    headerCtaRegister: 'Sign up',
    fromCountry: 'From',
    detailHero: 'The journey',
    inNumbers: '——— In numbers',
    timeline: '——— The timeline',
    timelineTitleA: 'Step by',
    timelineTitleB: 'step.',
    timelineSub: 'No success story is built overnight. Here is the journey — from first step to where we are today.',
    journeyEyebrow: '——— The journey',
    keyPointsEyebrow: '——— Highlights',
    inOwnWords: '——— In her own words',
    relatedEyebrow: '——— Other stories',
    relatedTitleA: 'More who have found',
    relatedTitleB: 'their place.',
    relatedSeeAll: 'See all stories',
    yourTurnEyebrow: '——— Your turn',
    yourTurnTitleA: 'The next story',
    yourTurnTitleB: 'could be yours.',
    yourTurnSub: 'Norvig walks with you from first application to first day on the job — and long after.',
    yourTurnPrimary: 'Start your journey',
    yourTurnSecondary: 'All stories',
    shareBtn: 'Share',
    shareTitle: 'Share this story',
    detailCtaRegister: 'Become the next story',
    notFoundEyebrow: '——— Story 404',
    notFoundTitle: 'We couldn’t find that story.',
    notFoundSub: 'The link may be wrong, or the story is not yet published.',
    notFoundCta: 'Back to all stories',
    pageTitleSuffix: 'Norvig',
  },
  onboarding: {
    welcome: {
      eyebrow: '——— Welcome',
      titleA: "Let's build",
      titleB: 'your profile.',
      lede: 'It takes about 4 minutes. We ask about your background, where you want to work, and a bit about your life — so we can match you with jobs that actually fit.',
      bullets: [
        { label: 'Precision matching', desc: 'We weigh both skills and preferences.' },
        { label: 'Roles where care is needed', desc: 'Nursing homes, home care and health centres — plus education and childcare.' },
        { label: 'Personal follow-up', desc: 'All the way — also after your first day at work.' },
      ],
      startCta: 'Start setup',
    },
    sidebar: {
      eyebrow: '——— Profile setup',
      titleA: 'A new',
      titleB: 'chapter.',
      intro: 'We take a few minutes to set up your profile. It helps us find jobs that fit your skills and your life.',
      start: 'Start',
      stepNOf: (n, total) => `Step ${String(n).padStart(2, '0')} of ${String(total).padStart(2, '0')}`,
      stepLabels: { 1: 'About you', 2: 'Background', 3: 'Preferences', 4: 'Your life' },
      savedAuto: 'Saved automatically',
      savedAutoHint: 'Your progress is saved for 7 days — you can safely finish later.',
      finishLater: 'Finish later',
      back: 'Back',
      next: 'Next',
      saveAndContinue: 'Save and continue',
      skip: 'Skip',
      saving: 'Saving …',
      finish: 'Finish profile',
      logout: 'Log out',
      logoutTitle: 'Log out now?',
      logoutBody: "You're in the middle of profile setup. We'll save your progress locally for 7 days so you can pick up where you left off next time you log in.",
      logoutCancel: 'Stay here',
      logoutConfirm: 'Log out',
      pause: 'Pause',
      pauseTooltip: 'Save progress and continue later',
      pauseToast: 'Progress saved — continue whenever you like.',
    },
    step1: {
      eyebrow: 'About you',
      title: 'Who are you?',
      subtitle: 'Let us start with the basics. These details help us tailor your matches.',
      nameLabel: 'Full name',
      namePlaceholder: 'e.g. Maria Santos',
      nameError: 'We need your name',
      nationalityLabel: 'Nationality',
      nationalityPlaceholder: 'Select country — or type to search',
      nationalitySearchPlaceholder: 'Search countries …',
      nationalityQuickPicks: 'Common choices',
      nationalityAllCountries: 'All countries',
      nationalityEmpty: 'No matches. Try a different search.',
      nationalityClear: 'Clear selection',
      nationalityError: 'Where are you from?',
      languagesLabel: 'Languages you master',
      languagesHint: 'Pick every language you master. Tap “Other” to add more.',
      languagesError: 'Select at least one language',
      otherToggle: 'Other',
      otherInputPlaceholder: 'Type a language …',
      otherInputAdd: 'Add',
      otherRemoveLabel: 'Remove language',
    },
  },
  profile: {
    pageEyebrow: '—— Profile',
    pageTitle: 'Your profile.',
    backToDashboard: 'Back to dashboard',
    editProfile: 'Edit profile',
    completenessLabel: 'Profile completeness',
    notFilledYet: 'Not filled in yet',
    fillIn: 'Fill in',
    fillInProfile: 'Fill in profile',
    completeTips: {
      title: 'Complete your profile',
      body: 'To increase matching opportunities, fill in these fields:',
      cta: 'Edit profile',
      progress: '{filled} of 10 fields filled · {missing} left',
    },
    availableFromLabel: 'Available from',
    availableImmediate: 'Immediately',
    sections: {
      personal: 'Personal information',
      information: 'Information',
      background: 'Background',
      preferences: 'Preferences',
      about: 'About me',
      documents: 'Documents',
    },
    fields: {
      fullName: 'Full name',
      companyName: 'Company name',
      nationality: 'Nationality',
      languages: 'Languages',
      familyStatus: 'Family status',
      familyInfo: 'Family',
      email: 'Email',
      phone: 'Phone',
      profession: 'Profession',
      sector: 'Sector',
      yearsExperience: 'Years of experience',
      experienceYears: 'Years of experience',
      certifications: 'Certifications & authorisations',
      skills: 'Skills',
      education: 'Education',
      workHistory: 'Work history',
      preferredShift: 'Preferred shift',
      employmentType: 'Employment type',
      locationPreferences: 'Preferred location',
      housingPreference: 'Housing',
      salaryExpectation: 'Salary expectation',
      availableFrom: 'Available from',
      interests: 'Interests',
    },
    emptyDocs: 'No documents uploaded yet.',
    uploadDocument: 'Upload document',
    nationalityFlagAlt: 'Flag for',
    titleNotSet: 'Profession not set',
    brreg: {
      orgnrLabel: 'Organisation number',
      orgnrPlaceholder: '9 digits',
      orgnrInvalid: 'Must be 9 digits',
      fetchCta: 'Fetch from BRREG',
      fetching: 'Fetching …',
      saveCta: 'Save',
      saving: 'Saving …',
      saveConfirmTitle: 'Confirm save',
      saveConfirmBody: 'Organisation number will be locked after saving. Change requires support.',
      saveConfirmYes: 'Save and lock',
      saveConfirmNo: 'Cancel',
      lockedNote: 'Organisation number is locked — ',
      lockedSupportLink: 'contact support to change',
      refreshCta: 'Refresh from BRREG',
      refreshing: 'Refreshing …',
      lastUpdated: 'Last updated',
      errors: {
        notFound: 'Organisation number not found in BRREG',
        unavailable: 'BRREG is unavailable right now. Try again shortly.',
        generic: 'Something went wrong. Try again.',
      },
      fields: {
        firmanavn: 'Company name',
        organisasjonsform: 'Legal form',
        registreringsstatus: 'Registration status',
        registreringsdato: 'Registration date',
        stiftelsesdato: 'Founded',
        mva_registrering: 'VAT registered',
        antall_ansatte: 'Employees',
        hjemmeside: 'Website',
        epostadresse: 'Email',
        telefonnummer: 'Phone',
        forretningsadresse: 'Business address',
        postadresse: 'Postal address',
        kommune: 'Municipality',
        fylke: 'County',
        land: 'Country',
        naeringskode: 'Industry code (NACE)',
        bransjebeskrivelse: 'Industry description',
      },
    },
    photo: {
      uploadCta: 'Upload photo',
      changeCta: 'Change photo',
      removeCta: 'Remove photo',
      uploading: 'Uploading …',
      successToast: 'Photo updated.',
      removedToast: 'Photo removed.',
      errorTooLarge: 'File too large. 5 MB max.',
      errorBadType: 'File format not supported. Use JPEG, PNG or WebP.',
      errorGeneric: 'Something went wrong during upload. Try again.',
      cropper: {
        title: 'Crop photo',
        hint: 'Drag to adjust · scroll or use the slider to zoom',
        zoomLabel: 'Zoom',
        rotate: 'Rotate 90°',
        skip: 'Skip cropping',
        cancel: 'Cancel',
        confirm: 'Use selection',
        processing: 'Processing …',
      },
    },
  },
  analytics: {
    eyebrow: '02 — Analytics',
    title: 'Numbers that',
    italicSuffix: 'speak.',
    subtitle: 'How your roles draw attention — and who is reaching back.',
    kpi: {
      applicationsLast7d: 'Applications · last 7 days',
      applicationsTotal: 'Applications · total',
      avgPerJob: 'Avg applications/job',
      activeConversations: 'Active conversations',
      verifiedCandidatesMarket: 'Verified candidates in market',
      verifiedCandidatesInSector: 'verified candidates in',
      activeJobs: 'Active roles',
    },
    charts: {
      perWeekTitle: 'Applications per week',
      perWeekSubtitle: 'Last 8 weeks',
      bySectorTitle: 'Applications by sector',
      bySectorSubtitle: 'Distribution across sectors',
      inSectorSubtitle: 'Applications in your sector',
      recentTitle: 'Recent applications',
      recentSubtitle: 'Last 10 applications, newest first',
      weekAxis: 'Week',
      countAxis: 'Count',
    },
    status: {
      pending: 'Pending',
      reviewed: 'Reviewed',
      contacted: 'Contacted',
      rejected: 'Rejected',
    },
    empty: {
      title: 'Get started',
      subtitle: 'Once applications start arriving, you’ll see numbers, trends and distribution here.',
    },
    loading: 'Loading analytics …',
    csv: {
      download: 'Export CSV',
      downloading: 'Downloading …',
      tooltip: 'Download as CSV (Excel-friendly)',
    },
    funnel: {
      eyebrow: 'Conversion funnel',
      subtitle: 'How engagement progresses, last 30 days',
      conversionLabel: 'of',
      tooltipPrefix: 'You made',
      tooltipSearches: 'searches',
      tooltipViews: 'profile views',
      tooltipContacts: 'conversations',
      tooltipDays: 'last 30 days',
    },
    topCandidates: {
      eyebrow: 'Most viewed',
      subtitle: 'Candidates you keep coming back to',
      timesSeen: 'Seen',
      timesSeenSuffix: 'times',
      lastViewed: 'last seen',
      emptyTitle: 'No views yet',
      emptySubtitle: 'Start exploring candidates to see trends here.',
    },
    topSkills: {
      eyebrow: 'Top skills',
      subtitle: 'What you’re searching for, last 30 days',
      searchedNTimes: 'searches',
      emptyTitle: 'No searches yet',
      emptySubtitle: 'When you start using filters in candidate search, your most-used will appear here.',
    },
    trendDaily: {
      eyebrow: 'Activity · 30 days',
      subtitle: 'Searches, views and new conversations day by day',
      searches: 'Searches',
      views: 'Views',
      contacts: 'Conversations',
    },
    shortlist: {
      eyebrow: 'Your shortlist',
      subtitle: 'Candidates you saved for follow-up',
      saveTooltip: 'Save to shortlist',
      unsaveTooltip: 'Remove from shortlist',
      saveSuccess: 'Added to shortlist',
      unsaveSuccess: 'Removed from shortlist',
      emptyTitle: 'No candidates yet',
      emptySubtitle: 'Click the heart on a candidate card to save them.',
      viewedNTimes: 'viewed',
      timesShort: 'x',
      cvLabel: 'CV',
      certCountSuffix: 'certificates',
      certCountSuffixSingular: 'certificate',
      remove: 'Remove',
      suggestionsEyebrow: 'Suggestions · based on your views',
      suggestionsCta: 'Save',
      suggestionsViewSuffix: 'views',
    },
  },
  dashboard: {
    marketIndex: {
      title: 'Market index in your sector',
      period: 'last 30 days',
      vsPrev: 'vs. previous 30 d',
      loading: 'Loading market data …',
    },
  },
  horisont: {
    eyebrowPrefix: 'Nº 06',
    label: 'Horizon',
    title: 'Horizon',
    italicSuffix: '— perspectives.',
    subtitle: 'Perspectives on people, talent and the future of work.',
    intro: 'We write from inside Norwegian recruitment — about structures that fail, models that work, and the people who hold it together.',
    featuredLabel: 'Featured article',
    moreComing: 'More articles coming soon.',
    minReadSuffix: 'min read',
    byAuthor: 'By',
    publishedOn: 'Published',
    tagsLabel: 'Tags',
    relatedTitle: 'Related articles',
    backToList: 'Back to Horizon',
    moreArticles: 'Read more articles',
    notFoundTitle: 'Article not found',
    notFoundBody: 'We couldn’t find the article you’re looking for. It may have moved — or be on its way.',
    notFoundCta: 'Back to Horizon',
    readArticleCta: 'Read the article',
    authorBioEyebrow: 'About the author',
    sourcesLabel: 'Sources',
    newsletter: {
      eyebrow: 'Get the next essay',
      title: 'Join the list.',
      subtitle: 'One essay per month on people, talent, and Norwegian work life. No spam.',
      placeholder: 'your@email.com',
      submit: 'Subscribe',
      submitting: 'Sending …',
      successTitle: 'Thanks!',
      successBody: 'You are on the list. We will write when the next essay is ready.',
      errorGeneric: 'Something went wrong. Please try again shortly.',
      errorInvalid: 'Please enter a valid email address.',
      errorRateLimit: 'Too many attempts. Please try again in a couple of minutes.',
      privacyNote: 'We never share your email. Unsubscribe anytime.',
    },
    share: {
      button: 'Share article',
      tooltip: 'Share article',
      copied: 'Link copied',
      failed: 'Could not share',
      rowLabel: 'Share this article',
      linkedin: 'Share on LinkedIn',
      email: 'Share by email',
    },
  },
  messages: {
    pageEyebrow: '01 — Direct contact',
    pageTitle: 'Conversations with',
    pageItalic: 'employers.',
    pageTitleCandidate: 'Conversations with',
    pageItalicCandidate: 'candidates.',
    pageSubtitle: 'Talk directly. No middlemen. No fees.',
    back: 'Dashboard',
    listHeader: 'Conversations',
    emptyListTitle: 'No conversations yet',
    emptyListBody: 'When you start a conversation with a candidate, it appears here.',
    emptyListBodyCandidate: 'When an employer contacts you, the conversation appears here.',
    selectTitle: 'Pick a conversation',
    selectBody: 'Choose a thread from the list to see messages.',
    inputPlaceholder: 'Type a message ...',
    sendBtn: 'Send',
    sending: 'Sending ...',
    employerLabel: 'Employer',
    candidateLabel: 'Professional',
    unreadBadge: 'new',
    unreadBadgePlural: 'new',
    archivedTag: 'Archived',
    archive: 'Archive conversation',
    archiveConfirm: 'Hide this conversation from your inbox? The other party will still see it.',
    archived: 'Conversation archived',
    archiveError: 'Could not archive the conversation',
    sendError: 'Could not send the message',
    quickReplyLabel: 'Quick replies:',
    quickReplyYes: 'Yes, feel free to share',
    quickReplyYesText: 'Yes, feel free — my documents are available to you.',
    quickReplyTellMore: 'Tell me more about the role',
    quickReplyTellMoreText: 'Thanks for your interest! Please tell me more about the role you have in mind.',
    showMap: 'Show map',
    hideMap: 'Hide map',
    loadError: 'Could not load conversations',
    adminAuditBanner: 'You can see every conversation on the platform. You can reply and start new conversations — your messages are marked as Norvig.',
    adminCantSend: 'Admin accounts cannot send messages',
    softDeletedForMe: 'Hidden for one party',
    startNew: {
      eyebrow: 'New conversation',
      title: 'Send a message to the candidate',
      subtitle: 'Your first message starts the conversation. Be specific about the role and why you are reaching out.',
      subtitleAdmin: 'You are writing on behalf of Norvig. The candidate will clearly see the message comes from the platform team.',
      placeholder: 'Hi, we have a role that matches your profile ...',
      submit: 'Send message',
      cancel: 'Cancel',
      sent: 'Message sent',
      tooShort: 'Type at least 1 character',
      crossSector: 'The candidate belongs to a different sector and cannot be contacted here.',
      missingSector: 'Set your organisation sector before sending messages.',
      notFound: 'The candidate could not be found.',
      genericError: 'Something went wrong. Please try again.',
    },
    newMessageToast: 'You have a new message',
    sendCta: 'Send message',
  },

  terms: {
    pageTitle: 'Terms of Service',
    pageEyebrow: 'Nº 11 — TERMS OF SERVICE',
    lastUpdatedLabel: 'Last updated',
    versionLabel: 'Version',
    backLabel: 'Back',
    questionsHeading: 'Questions?',
    questionsBody: 'Email us at',
    signupCheckboxBefore: 'I have read and accept Norvig\u2019s',
    signupCheckboxLink: 'terms of service',
    signupCheckboxRequired: 'You must accept the terms of service before continuing.',
    profile: {
      heading: 'Terms of service',
      acceptedOn: 'You accepted terms version {version} on {date}',
      neverAccepted: 'You have not accepted the terms yet.',
      lastViewed: 'Last viewed by you: {date}',
      neverViewed: 'You have not opened the terms after acceptance.',
      currentVersion: 'Current version at Norvig: {version}',
      viewButton: 'View terms of service',
    },
    modal: {
      heading: 'Updated terms of service',
      subtext: 'We have updated our terms. Please read and accept to continue.',
      accept: 'I accept',
      openInNewTab: 'Open in new tab',
      acceptHint: 'The button activates once you have scrolled to the end.',
      submitting: 'Saving \u2026',
    },
  },

  employment: {
    heading: 'Report employment',
    subtext: 'Obligation under §5 of the terms of service — notify within 40 days of starting employment.',
    openButton: 'Report new employment',
    list: {
      heading: 'My reported employments',
      empty: 'You have no reported employments yet.',
      cols: {
        employer: 'Employer',
        position: 'Position',
        start: 'Start date',
        type: 'Type',
        source: 'Contact source',
        submitted: 'Reported',
        status: 'Status',
      },
      statusSubmitted: 'Submitted',
      statusReviewed: 'Seen by Norvig',
    },
    types: {
      fast: 'Permanent',
      midlertidig: 'Temporary',
      vikar: 'Locum',
      freelance: 'Freelance',
      annet: 'Other',
    },
    sources: {
      via_norvig: 'Through Norvig',
      outside_norvig: 'Outside Norvig',
    },
    form: {
      title: 'Report new employment',
      subtitle: 'Fill in the details of your employment. We do not notify the employer — your report is confidential.',
      employerName: 'Employer name',
      employerNamePlaceholder: 'E.g. Riksrevisjonen, Helse Bergen',
      employerOrgnr: 'Org. no. (optional)',
      employerOrgnrPlaceholder: '9 digits',
      norvigMemberBadge: 'Norvig member',
      positionTitle: 'Position title',
      positionTitlePlaceholder: 'E.g. Nurse, Care worker',
      startDate: 'Start date',
      employmentType: 'Employment type',
      contactSource: 'Contact created via',
      notes: 'Additional information (optional)',
      notesPlaceholder: 'Any remarks \u2026',
      cancel: 'Cancel',
      submit: 'Submit report',
      submitting: 'Submitting \u2026',
      success: 'Thank you \u2014 Norvig has received your report.',
      errorGeneric: 'Could not submit. Please try again.',
      errorRateLimit: 'You have reached today\u2019s limit of 5 reports. Try again tomorrow.',
      brregLookupNotFound: 'Organisation not found',
      brregLookupOk: 'Verified in the Norwegian Business Register',
    },
  },

  admin: {
    compliance: {
      eyebrow: 'Nº 03 — NOTIFICATION OBLIGATION · TOS',
      title: 'Compliance',
      italicSuffix: 'snapshot.',
      subtitle: 'Real-time view of terms acceptance and submitted employment reports.',
      tiles: {
        acceptanceRate: 'ACCEPTANCE RATE',
        acceptanceRateHint: '{n} of {total} active workers have accepted the current version.',
        pending: 'PENDING ACCEPTANCE',
        pendingHint: 'Active workers must accept at next sign-in.',
        unreviewed: 'NOT REVIEWED',
        unreviewedHint: 'Employment reports waiting for admin review.',
        unreviewedCta: 'Open queue',
        outside: 'EXTERNAL CONTACTS · 30D',
        outsideHint: 'Employments started outside the portal in the last 30 days.',
        employerAcceptanceRate: 'EMPLOYER ACCEPTANCE',
        employerAcceptanceHint: '{n} of {total} active employers have accepted the current version.',
        employerPending: 'EMPLOYERS PENDING',
        employerPendingHint: 'Active employers must accept at next sign-in.',
        hireReportsUnreviewed: 'HIRE REPORTS · NOT REVIEWED',
        hireReportsUnreviewedHint: 'Hire reports waiting for admin review.',
        hireReportsPendingFee: 'FEE AWAITING INVOICING',
        hireReportsPendingFeeHint: 'Hire reports with fee_status=pending.',
      },
      footer: {
        exempt: '{n} workers exempt (inactive > 2 years)',
        openAdmin: 'Open admin page',
      },
      email: {
        sendNow: 'Send summary now',
        confirmTitle: 'Send compliance summary now?',
        confirmBody: 'You will dispatch a daily summary email to every admin user. The action is recorded in the audit trail.',
        confirmCancel: 'Cancel',
        confirmConfirm: 'Send now',
        sending: 'Sending …',
        successSent: 'Summary delivered to {n} admin(s).',
        successDevMode: 'Dev mode — summary logged to console for {n} admin(s). Set RESEND_API_KEY in .env to enable real send.',
        rateLimit: 'Hold on — you have already sent 3 times this hour.',
        genericError: 'Could not send summary.',
        openLog: 'Open email log',
      },
    },
    emailLog: {
      pageEyebrow: 'NORVIG · ADMIN',
      title: 'Email log',
      italicSuffix: 'audit trail.',
      subtitle: 'Every dispatched and dev-logged system message with a snapshot of the data at send time.',
      filters: {
        type: 'Type',
        status: 'Status',
        from: 'From date',
        to: 'To date',
        all: 'All',
        apply: 'Apply filters',
        reset: 'Reset',
      },
      types: {
        daily_compliance_summary: 'Daily compliance summary',
        terms_update_notification: 'Terms update',
      },
      statuses: {
        sent: 'Sent',
        dev_logged: 'Dev-logged',
        failed: 'Failed',
      },
      tracking: {
        delivered: 'Delivered',
        opened: 'Opened',
        clicked: 'Clicked',
        bounced: 'Bounced',
        lastClicked: 'Last clicked link',
        hint: 'Open/click tracking requires a Resend webhook (events: delivered, opened, clicked, bounced) pointing to:',
        suppress: 'Skip in announcements',
        suppressed: 'Skipped',
        suppressedInfo: 'This address is skipped in announcements.',
        unsuppress: 'Undo',
      },
      cols: {
        sentAt: 'Time',
        type: 'Type',
        recipient: 'Recipient',
        subject: 'Subject',
        status: 'Status',
        triggeredBy: 'Triggered by',
        detail: 'Detail',
      },
      triggeredBy: {
        scheduled: 'Scheduled (cron)',
        manual_admin: 'Manual (admin)',
      },
      detail: {
        bodyPreview: 'Body preview',
        snapshot: 'Data snapshot at send',
        provider: 'Provider',
        providerMessageId: 'Message ID',
        error: 'Error',
        close: 'Close detail',
      },
      empty: 'No emails sent yet.',
      back: 'Back to admin',
      pagination: {
        showing: 'Showing {from}–{to} of {total}',
        prev: 'Previous',
        next: 'Next',
      },
    },
    documents: {
      pageEyebrow: 'NORVIG · ADMIN',
      title: 'User documents',
      italicSuffix: 'access & audit.',
      subtitle: 'All documents uploaded by professionals — with access stats and hard-delete for GDPR requests.',
      filters: {
        ownerEmail: 'Owner email',
        category: 'Category',
        mimeType: 'File type',
        includeDeleted: 'Include deleted',
        all: 'All',
        apply: 'Apply filters',
        reset: 'Reset',
      },
      cols: {
        owner: 'Owner',
        category: 'Category',
        filename: 'Filename',
        size: 'Size',
        uploaded: 'Uploaded',
        views30d: 'Views (30d)',
        downloads30d: 'Downloads (30d)',
        actions: 'Action',
      },
      actions: {
        viewLog: 'View log',
        hardDelete: 'Hard delete',
        exportCsv: 'Export CSV',
      },
      accessLog: {
        heading: 'Access log',
        empty: 'No access records.',
        cols: {
          when: 'Time',
          who: 'User',
          role: 'Role',
          type: 'Type',
          ip: 'IP',
        },
        types: {
          view_metadata: 'View',
          download: 'Download',
          preview: 'Preview',
        },
      },
      hardDeleteConfirm: 'Permanently delete this document and remove the file from disk? This action cannot be undone.',
      empty: 'No documents recorded.',
      back: 'Back to admin',
    },
    tenders: {
      pageEyebrow: 'NORVIG · ADMIN',
      title: 'Tender monitoring',
      italicSuffix: 'Doffin + admin status.',
      subtitle: 'Daily Doffin sync filtered against the Norvig CPV whitelist. Track status, take notes, export to CSV.',
      lastFetch: 'Last fetch {dt}',
      lastFetchSummary: '{n} new · {u} updated · {e} expired',
      refreshNow: 'Refresh now',
      refreshing: 'Refreshing …',
      exportCsv: 'Export CSV',
      filters: {
        status: 'Status',
        sector: 'Sector',
        cpv: 'CPV code',
        search: 'Search',
        all: 'All',
        apply: 'Apply filters',
        reset: 'Reset',
      },
      cols: {
        status: 'Status',
        title: 'Title',
        issuer: 'Issuer',
        cpv: 'CPV',
        sector: 'Sector',
        published: 'Published',
        deadline: 'Deadline',
        value: 'Value',
        actions: 'Action',
      },
      empty: 'No tenders in the database yet. Click "Refresh now" to trigger a sync.',
      back: 'Back to admin',
      detail: {
        description: 'Description',
        cpvCodes: 'CPV codes',
        doffinUrl: 'Doffin link',
        statusLabel: 'Set status',
        notesLabel: 'Internal notes',
        save: 'Save',
        saving: 'Saving …',
        saved: 'Saved',
        cancel: 'Cancel',
      },
    },
    tenderInquiries: {
      pageEyebrow: 'NORVIG · ADMIN',
      title: 'Tender leads',
      italicSuffix: 'public inquiries.',
      subtitle: 'Leads from the "Get in touch" button on the tender page. Track status and notes until conversion.',
      empty: 'No inquiries yet.',
      back: 'Back to admin',
      exportCsv: 'Export CSV',
      filters: {
        status: 'Status',
        search: 'Search',
        all: 'All',
        apply: 'Apply filters',
        reset: 'Reset',
      },
      cols: {
        status: 'Status',
        sender: 'Sender',
        company: 'Company',
        tender: 'Tender',
        received: 'Received',
        phone: 'Phone',
        email: 'Email',
        actions: 'Action',
      },
      detail: {
        message: 'Message',
        statusLabel: 'Set status',
        notesLabel: 'Internal notes',
        save: 'Save',
        saving: 'Saving …',
      },
      status: {
        new: 'New',
        contacted: 'Contacted',
        qualified: 'Qualified',
        converted: 'Converted',
        dismissed: 'Dismissed',
      },
      convertButton: 'Convert to customer →',
      alreadyConverted: 'Already converted',
      goToCustomer: 'Go to customer →',
      convertModal: {
        title: 'Convert to customer in pipeline',
        intro: 'The following information will be used to create the employer record:',
        company: 'Company',
        contact: 'Contact',
        emailLabel: 'Email',
        phone: 'Phone',
        phoneMissing: 'Not provided',
        fromTender: 'From tender',
        doffinRef: 'Doffin ref',
        inquiryReceived: 'Inquiry received',
        statusLabel: 'Status on creation',
        priorityLabel: 'Priority',
        notesLabel: 'Additional note (optional)',
        notesPlaceholder: 'E.g. "Send contract by email tomorrow"',
        cancel: 'Cancel',
        confirm: 'Create customer →',
        creating: 'Creating …',
        successToast: (name) => `${name} added to pipeline.`,
        existingToast: (name) => `${name} already existed — attribution added.`,
        errorToast: 'Could not create customer.',
      },
    },
    institutions: {
      back: 'Back to admin',
      title: 'Institution registry',
      titleSuffix: 'health & care.',
      subtitle: 'National registry of municipal health and care institutions from the Brønnøysund Register Centre (industry codes 87.101–87.104), classified by type and enriched with contact info from the municipality where the institution lacks its own.',
      lastSynced: 'Last synced',
      sync: 'Sync from Brønnøysund',
      syncRunning: 'Syncing…',
      exportCsv: 'Export CSV',
      searchPlaceholder: 'Search name, municipality, parent unit or org. no. …',
      allTypes: 'All types',
      allCodes: 'All industry codes',
      contactAll: 'Contact info: all',
      contactWith: 'With contact info',
      contactWithout: 'Without contact info',
      rows: 'institutions',
      viaKommune: 'via municipality',
      autoSync: 'Nightly auto-sync (03:00)',
      mapShow: 'Show map',
      mapHide: 'Hide map',
      mapCount: (n, k) => `${n} institutions across ${k} municipalities`,
      mapDot: (n) => `${n} institution${n === 1 ? '' : 's'}`,
      pipelineAdd: 'To pipeline',
      pipelineAdded: 'In pipeline',
      pipelineCol: 'Lead',
      emptyNoSync: 'The registry is empty — run "Sync from Brønnøysund" to import all institutions.',
      emptyFiltered: 'No institutions match the filters.',
      sourceNote: 'Source: the Brønnøysund Register Centre open API for subunits. Green icon = the institution\'s own contact info; grey = inherited from the parent unit (municipality). Classification is name-based and indicative.',
      stats: {
        total: 'Institutions',
        sykehjem: 'Nursing homes',
        omsorg: 'Care centres',
        kommuner: 'Municipalities',
        employees: 'Total employees',
        contact: 'Contact coverage',
      },
      cols: {
        name: 'Institution',
        type: 'Type',
        kommune: 'Municipality',
        parent: 'Parent unit',
        employees: 'Employees',
        contact: 'Contact',
      },
      send: {
        selected: (n) => `${n} selected`,
        cta: 'Send presentation',
        clear: 'Clear selection',
        title: 'Send presentation email',
        desc: (n) => `Will be sent to ${n} selected institutions. Institutions without an email address are skipped automatically.`,
        body: 'The email presents Norvig as a recruitment channel for health and care and links to /for-kommuner. Institutions with their own email receive it directly — otherwise it goes to the municipality inbox.',
        testLabel: 'Send a test first (optional)',
        testBtn: 'Send test',
        testOk: 'Test email sent — check your inbox.',
        confirm: (n) => `Send to ${n} institutions`,
        sending: 'Sending …',
        result: (s, f, k, a) => `Sent: ${s} · Failed: ${f} · Skipped (no email): ${k}${a > 0 ? ` · Recently presented (last 30 days): ${a}` : ''}`,
        cancel: 'Cancel',
        sentBadge: (d) => `Presented ${d}`,
      },
      detail: {
        fields: 'Key fields',
        contact: 'Contact info',
        own: 'own',
        viaParent: 'from municipality',
        address: 'Address',
        kommune: 'Municipality',
        parent: 'Parent unit',
        employees: 'Employees',
        codes: 'Industry codes',
        founded: 'Founded',
        registered: 'Registered',
        synced: 'Last synced',
        email: 'Email',
        phone: 'Phone',
        mobile: 'Mobile',
        inactive: 'Inactive (removed from BRREG)',
        presentation: 'Presentation sent',
        presentationTo: 'to',
        presentationBy: 'by',
        pipeline: 'Pipeline',
        notInPipeline: 'Not in the pipeline yet.',
        addToPipeline: 'To pipeline',
        openPipeline: 'Open in pipeline →',
        priority: 'Priority',
        history: 'History',
        noHistory: 'No history yet.',
        notes: 'Notes',
        noNotes: 'No notes yet.',
        notePlaceholder: 'Write a note …',
        addNote: 'Add',
        deleteNote: 'Delete note',
        close: 'Close',
      },
      coverage: {
        show: 'Show coverage report',
        hide: 'Hide coverage report',
        title: 'Coverage report',
        subtitle: 'Share of institutions Norvig has reached — in the pipeline or contacted with a presentation. Track the trend as a monthly growth target.',
        reached: 'Total coverage',
        reachedCount: 'Reached / total',
        inPipeline: 'In pipeline',
        contacted: 'Contacted',
        monthlyTitle: 'Trend last 12 months (cumulative reached)',
        colKommune: 'Municipality',
        colTotal: 'Institutions',
        colPipeline: 'Pipeline',
        colContacted: 'Contacted',
        colPct: 'Coverage',
        showAll: (n) => `Show all municipalities (+${n})`,
        empty: 'No institutions reached yet — add institutions to the pipeline or send presentations.',
        weeklyMail: 'Weekly coverage mail (Mon 08:15)',
        sendNow: 'Send to me now',
        sendNowBusy: 'Sending …',
        sendNowOk: (to) => `Sent to ${to}`,
      },
      sendLog: {
        show: 'Show send log',
        hide: 'Hide send log',
        title: 'Send log',
        subtitle: 'Every presentation email sent, with delivery, open and click status — and whether the institution has booked a chat.',
        stats: {
          sent: 'Total sent',
          openRate: 'Open rate',
          clicked: 'Clicked',
          bounced: 'Bounced',
          booked: 'Booked chat',
          reminded: 'Reminded',
        },
        cols: {
          institution: 'Institution',
          recipient: 'Recipient',
          sentAt: 'Sent',
          status: 'Status',
        },
        chips: {
          delivered: 'Delivered',
          opened: 'Opened',
          clicked: 'Clicked',
          bounced: 'Bounce',
          booked: 'Booked chat',
          pending: 'Sent',
        },
        by: 'by',
        empty: 'No presentations sent yet — select institutions in the table and click "Send presentation".',
        loadMore: (n) => `Show more (${n} left)`,
        openNote: 'Open and click data comes from Resend webhooks and may take a few minutes after the recipient opens the email.',
        remind: 'Send reminder',
        remindBusy: 'Sending …',
        remindedChip: (d) => `Reminded ${d}`,
        needsFollowup: '14+ days',
      },
    },
    employersPipeline: {
      pageEyebrow: 'NORVIG · ADMIN',
      title: 'Employer overview',
      italicSuffix: 'CRM pipeline.',
      subtitle: 'A single view of every registered employer with status, priority, and full history.',
      back: 'Back to admin',
      exportCsv: 'Export CSV',
      manageStatuses: 'Manage statuses',
      filters: {
        status: 'Status',
        sector: 'Sector',
        priority: 'Priority',
        source: 'Source',
        search: 'Search name / org-no / email',
        inactiveDays: 'Last login before',
        all: 'All',
        apply: 'Apply',
        reset: 'Reset',
        anyDays: 'Any time',
        days7: '7 days ago',
        days30: '30 days ago',
        days90: '90 days ago',
      },
      sourceLabels: {
        direct: 'Direct sign-up',
        tender_inquiry: 'From tender',
        manual_admin: 'Manually added',
      },
      acquisitionBadge: '★ From tender',
      prospectBadge: 'Prospect',
      attributionBlock: {
        title: 'Acquisition info',
        source: 'Source',
        tender: 'Tender',
        doffinRef: 'Doffin ref',
        inquiryFrom: 'Inquiry received',
        acquiredAt: 'Added to pipeline',
        viewInquiry: 'View original inquiry →',
      },
      priority: {
        low: 'Low',
        normal: 'Normal',
        high: 'High',
      },
      cols: {
        status: 'Status',
        org: 'Employer',
        orgnr: 'Org-no',
        sector: 'Sector',
        registered: 'Registered',
        lastLogin: 'Last login',
        priority: 'Priority',
        actions: 'Action',
      },
      detail: {
        emailLabel: 'Email',
        statusLabel: 'Set status',
        priorityLabel: 'Priority',
        notesLabel: 'Internal notes',
        changeNoteLabel: 'Change note (optional)',
        save: 'Save',
        saving: 'Saving …',
        history: 'Status history',
        noHistory: 'No history yet.',
        assignedAt: 'Assigned',
        assignedBy: 'By',
      },
      empty: 'No employers match the filter.',
      total: (n) => `${n} employer${n === 1 ? '' : 's'}`,
      page: 'Page',
      of: 'of',
      prev: 'Previous',
      next: 'Next',
    },
    employerStatuses: {
      pageEyebrow: 'NORVIG · ADMIN',
      title: 'CRM statuses',
      italicSuffix: 'pipeline configuration.',
      subtitle: 'Maintain the statuses used to track employers through the sales/agreement journey.',
      back: 'Back to pipeline',
      addNew: 'New status',
      cols: {
        order: 'Order',
        slug: 'Slug',
        labelNo: 'Norwegian label',
        labelEn: 'English label',
        color: 'Color',
        default: 'Default',
        system: 'System',
        actions: 'Action',
      },
      form: {
        slug: 'Slug (a-z, 0-9, _)',
        labelNo: 'Norwegian label',
        labelEn: 'English label',
        color: 'Color',
        order: 'Order',
        isDefault: 'Default for new employers',
        save: 'Save',
        saving: 'Saving …',
        cancel: 'Cancel',
        delete: 'Delete',
        confirmDelete: (label) => `Delete status "${label}"? This cannot be undone.`,
        systemLocked: 'System statuses cannot be deleted (but can be edited).',
      },
      colorLabels: {
        blue: 'Blue',
        amber: 'Amber',
        emerald: 'Emerald',
        purple: 'Purple',
        slate: 'Slate',
        rose: 'Rose',
        teal: 'Teal',
        indigo: 'Indigo',
        gray: 'Gray',
      },
    },
    employmentReports: {
      title: 'Employment reports',
      subtitle: 'Worker-reported employments under §5 of the terms of service.',
      filters: {
        from: 'From date',
        to: 'To date',
        contactSource: 'Source',
        norvigMember: 'Norvig member',
        reviewed: 'Status',
        all: 'All',
        unreviewed: 'Not reviewed',
        reviewed_: 'Reviewed',
        yes: 'Yes',
        no: 'No',
        apply: 'Apply filters',
        reset: 'Reset',
      },
      cols: {
        worker: 'Worker',
        employer: 'Employer',
        orgnr: 'Org. no.',
        position: 'Position',
        start: 'Start',
        type: 'Type',
        source: 'Source',
        submitted: 'Reported',
        member: 'Member',
        status: 'Status',
      },
      empty: 'No reports match the filters.',
      markReviewed: 'Mark as reviewed',
      reviewed: 'Reviewed',
      adminNotes: 'Notes',
      save: 'Save',
      exportCsv: 'Export CSV',
      totalLabel: 'Total',
      unreviewedLabel: 'Not reviewed',
    },
    hireReports: {
      pageEyebrow: 'NORVIG · ADMIN',
      title: 'Reported hires',
      italicSuffix: 'audit.',
      subtitle: 'Employers must notify Norvig within 40 days after hiring a candidate. This table lists every reported hire with fee status and admin actions.',
      back: 'Back to admin',
      exportCsv: 'Export CSV',
      empty: 'No reported hires match these filters yet.',
      filters: {
        sector: 'Sector',
        feeStatus: 'Fee status',
        reviewed: 'Reviewed?',
        source: 'Contact source',
        all: 'All',
        yes: 'Yes',
        no: 'No',
        apply: 'Apply filters',
        reset: 'Reset',
      },
      statuses: {
        pending: 'Pending',
        invoiced: 'Invoiced',
        paid: 'Paid',
        waived: 'Waived',
        disputed: 'Disputed',
      },
      sources: { viaNorvig: 'Via Norvig', outsideNorvig: 'Outside Norvig' },
      detail: {
        employer: 'Employer', position: 'Position', startDate: 'Start date',
        employmentType: 'Employment type', contactSource: 'Contact source',
        salary: 'Annual salary', notes: 'Notes', breakdown: 'Fee breakdown', months: 'mo',
      },
      actions: {
        markReviewed: 'Mark as reviewed',
        changeStatusTo: 'Change status to',
        reviewed: 'Marked as reviewed',
        statusUpdated: 'Status updated',
        error: 'Could not update',
        csvError: 'CSV export failed',
      },
      showing: 'Showing {from}–{to} of {total}',
      prev: 'Previous', next: 'Next',
    },
    sectorFees: {
      pageEyebrow: 'NORVIG · ADMIN',
      title: 'Sector fees',
      italicSuffix: 'pricing.',
      subtitle: 'Maintain placement-fee rates per sector. Changes are recorded in the audit trail and take effect immediately.',
      back: 'Back to admin',
      edit: 'Edit',
      editTitle: 'Edit sector fee',
      perm: 'Permanent',
      temp: 'Temporary',
      invoicing: 'Invoicing',
      probation: 'Probation (months)',
      terms: 'Payment terms',
      daysNet: 'days net',
      notesNo: 'Notes (NO)',
      maxCap: 'Max {n}',
      minMonths: 'min. {n} mo',
      minMonthsPlaceholder: 'min. months',
      capPlaceholder: 'Max NOK',
      feeNone: 'No fee',
      updatedBy: 'Last updated',
      models: { percentage: 'Percentage', flat: 'Flat sum', hybrid: 'Percentage + cap', none: 'No fee' },
      triggers: {
        after_probation: 'After probation',
        contract_signed: 'On contract signing',
        first_salary: 'On first salary',
        split_50_50: '50/50 (signing + probation)',
      },
      actions: { save: 'Save', cancel: 'Cancel', saved: 'Saved', error: 'Could not save' },
    },
    terms: {
      pageEyebrow: 'NORVIG · ADMIN',
      title: 'Terms of service',
      italicSuffix: 'version management.',
      subtitle: 'Maintain ToS text for both professional and employer. Save drafts first, publish when ready, and flag if users must re-accept.',
      back: 'Back to admin',
      tabs: { professional: 'Professional', employer: 'Employer' },
      current: 'Current version',
      noCurrent: 'No published version yet',
      drafts: 'Drafts',
      noDrafts: 'No drafts for this document type + locale.',
      history: 'History',
      noHistory: 'No previous versions.',
      preview: 'Preview',
      view: 'View',
      createDraft: 'Create new version',
      continueEditing: 'Continue',
      delete: 'Delete',
      saveDraft: 'Save draft',
      publish: 'Publish',
      close: 'Close',
      autosaved: 'Saved',
      editor: { untitled: 'Untitled', markdown: 'Markdown', preview: 'Live preview' },
      confirm: { deleteDraft: 'Delete this draft permanently?' },
      publishConfirm: {
        eyebrow: 'PUBLISH NEW VERSION',
        title: 'Ready to publish?',
        subtitle: 'The current version will be archived automatically.',
        version: 'Version (semver, e.g. 1.1)',
        summary: 'Change summary (visible in audit)',
        requiresReacceptance: 'Require users to re-accept',
        warning: 'All relevant users will see a forced modal on next login.',
        estimateLabel: 'Affected users',
        inactiveExcluded: '{n} excluded (2-year inactive)',
        notification: {
          checkbox: 'Send email notification to affected users',
          helpText: 'Users receive a short, friendly email explaining the change and why they will be asked to re-accept on next login.',
          warning: 'Users will see the forced modal with no prior notice.',
        },
        cancel: 'Cancel',
        confirm: 'Publish now',
      },
      actions: {
        draftCreated: 'Draft created',
        draftSaved: 'Draft saved',
        draftDeleted: 'Draft deleted',
        published: 'Published as v{v}',
        publishedWithReset: 'Published as v{v} · {n} users must re-accept',
        notificationSent: '{count} notifications dispatched',
        notificationSkipped: 'Notification disabled',
      },
      errors: {
        noBase: 'No current version to base draft on.',
        draftCreateFailed: 'Could not create draft.',
        draftSaveFailed: 'Could not save draft.',
        deleteFailed: 'Could not delete draft.',
        publishFailed: 'Publish failed.',
      },
    },
  },
  pricing: {
    eyebrow: 'NORVIG · PRICING',
    title: 'Transparent fees',
    italicSuffix: 'per sector.',
    subtitle: 'Norvig only charges a placement fee on a confirmed hire. Registration, candidate-database search and messaging are free of charge.',
    bullets: [
      'Fee obligation lapses after 2 years of inactivity.',
      'Invoicing trigger and probation rules vary per sector.',
      'Volume agreements or special partnerships — contact us directly.',
    ],
    back: 'Back',
    permanent: 'Permanent hire',
    temporary: 'Temporary / locum',
    invoicing: 'Invoicing',
    paymentTerms: 'Payment terms',
    paymentTermsValue: '{n} days net',
    ofGrossSalary: 'of first-year gross salary',
    maxCap: 'capped at {n}',
    minDuration: 'min. {n} months',
    feeNone: 'No fee',
    triggers: {
      after_probation: 'After probation ({n} months)',
      contract_signed: 'On contract signing',
      first_salary: 'On first salary',
      split_50_50: '50% signing · 50% after probation',
    },
    loading: 'Fetching pricing …',
    empty: 'No active sector rates published yet.',
    cta: {
      eyebrow: 'VOLUME · STRATEGIC PARTNERSHIPS',
      title: 'Custom arrangement?',
      body: 'Hiring regularly across multiple sectors or have specific needs? We tailor models — contact us for a chat.',
      button: 'Contact us',
      termsLink: 'See terms of service',
    },
    effectiveFrom: 'Rates effective from each sector card\'s published date.',
  },
  employer: {
    terms: {
      signupCheckbox: 'I have read and accept Norvig\'s terms of service for employers.',
      signupCheckboxLink: 'Read the terms',
      profileHeading: 'Terms of service — employer',
    },
    hireReport: {
      eyebrow: 'Nº 05 — NOTIFICATION OBLIGATION · REPORT HIRE',
      title: 'Report hire',
      subtitle: 'Obligation under clause 5 of the terms — notify within 40 days of hire.',
      openButton: 'Report new hire',
      modalEyebrow: 'NEW REPORT',
      modalTitle: 'Report hire',
      modalSubtitle: 'This information is used for audit and automatic fee estimation.',
      close: 'Close',
      cancel: 'Cancel',
      submit: 'Submit report',
      submitting: 'Sending …',
      submittedWithFee: 'Thank you — report received. Estimated fee: {fee}.',
      submittedNoFee: 'Thank you — report received. Fee will be calculated when salary is known.',
      fields: {
        candidate: 'Candidate',
        searchInDb: 'Search Norvig database',
        notInDb: 'Not in Norvig database',
        searchPlaceholder: 'Name or email (min. 2 chars)',
        namePlaceholder: 'Type the worker\'s full name',
        changeCandidate: 'Change candidate',
        position: 'Position title',
        startDate: 'Start date',
        employmentType: 'Employment type',
        durationMonths: 'Contract duration (months)',
        salary: 'Gross annual salary',
        salaryHint: '(optional, for fee calculation)',
        contactSource: 'First contact via',
        notes: 'Additional info (optional)',
        types: { fast: 'Permanent', midlertidig: 'Temporary', vikar: 'Locum', freelance: 'Freelance' },
        sources: {
          viaNorvig: 'Via Norvig',
          outsideNorvig: 'Outside Norvig (referral, network, etc.)',
        },
      },
      feeEstimate: {
        heading: 'ESTIMATED FEE',
        unknownSalary: 'Enter annual salary to see an estimate. The fee will be calculated at invoicing in any case.',
        none: 'No fee.',
        belowMin: 'Below {n} months — no fee.',
        flat: 'flat fee',
        percentage: '{rate}% of the stated annual salary.',
        capped: '{rate}% = {pre} → cap {cap} (applied).',
        noSector: 'No sector set on the employer account yet.',
      },
      list: {
        empty: 'You have no reported hires yet.',
        statuses: {
          pending: 'Pending', invoiced: 'Invoiced', paid: 'Paid',
          waived: 'Waived', disputed: 'Disputed',
        },
      },
      errors: {
        rateLimit: 'You can report a maximum of 10 hires per day. Try again tomorrow.',
        generic: 'Could not submit report.',
      },
    },
  },
  // ─── Facelift Phase 2 — Editorial Landing (namespace: landingV2) ──────
  landingV2: {
    hero: {
      eyebrow: 'Recruitment to Norway',
      headline: 'Create your new life\nin Norway.',
      subCopy:
        'Norvig connects nurses, care workers and other professionals with Norwegian municipalities who value competence, motivation, and lasting relationships.',
      ctaPrimary: 'Sign up — free',
      ctaSecondary: 'For employers',
    },
    trust: {
      eyebrow: 'Trust through structure',
      body:
        'Norvig complies with GDPR, Norwegian Directorate of Health credentialing, and Norwegian labour standards.',
      logos: ['Helsedirektoratet', 'DSB', 'NAV', 'Skatteetaten', 'GDPR'],
    },
    value: {
      pro: {
        eyebrow: 'For professionals',
        title: 'Find work where your\ncare is valued.',
        body:
          'No middlemen. No noise. Just direct contact with employers who need you — in nursing homes, home care and health centres.',
        points: [
          'Verified credentials and documentation',
          'Direct conversations — no agent in between',
          'Support for relocation and Norwegian working life',
        ],
        cta: 'How it works for professionals',
      },
      emp: {
        eyebrow: 'For employers',
        title: 'Candidates that\nfit your workplace.',
        body:
          'Norvig curates health professionals and educators with the right credentials and expectations. You spend time choosing, not filtering.',
        points: [
          'Pre-verified candidates',
          'Sector-specific curation',
          'Open dialogue from the first conversation',
        ],
        cta: 'How it works for employers',
      },
    },
    how: {
      eyebrow: 'The process',
      title: 'From sign-up to first day of work.',
      steps: [
        { title: 'Create profile',        body: 'Add your documentation, languages and preferences.' },
        { title: 'Get verified',           body: 'We confirm credentials and background before you go live.' },
        { title: 'Match with employers',   body: 'Meet Norwegian employers who see your profile directly.' },
        { title: 'Sign contract',          body: 'We support you through contract, relocation and onboarding.' },
      ],
    },
    sectors: {
      eyebrow: 'Sectors',
      title: 'Where we make a difference.',
      ctaAll: 'Explore all sectors',
    },
    testimonial: {
      quote:
        'Norvig gave me more than a job. It gave me a safe start in a new country, and an employer who understood how to welcome someone.',
      attribution: 'Maria K. — ICU Nurse, Oslo University Hospital',
      meta: 'Hired via Norvig, 2025',
    },
    stories: {
      eyebrow: 'Stories',
      heading: 'People who found their place.',
      subCopy:
        'Real journeys from around the world to Norwegian workplaces. No recipes — just paths that worked.',
      readMore: 'Read the story',
    },
    faq: {
      eyebrow: 'Frequently asked',
      heading: 'The most common questions.',
    },
    norway: {
      eyebrow: 'About Norway',
      title: 'A country built on trust,\nnature and pride in craft.',
      body:
        'Norway is not only fjords and mountains. It is short hierarchies, world-class labour rights, and a society that looks after its own.',
      cta: 'Read more about Norway',
    },
    finalCta: {
      title: 'Your next chapter starts here.',
      subCopy: 'Sign up free — take the first step toward work in Norway.',
      ctaPrimary: 'Sign up — free',
      loginPrompt: 'Already have an account?',
      loginLink: 'Log in',
    },
  },
};

export default en;
